import React from 'react';
import { Typography, Box, Paper, Button, CircularProgress, Alert, AlertTitle, Tooltip, Link } from '@mui/material';
import { Routes, Route } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Login, Error, PrivacyTip } from '@mui/icons-material';
import Navigation from './Navigation.jsx';
import Settings from './Settings.jsx';
import WaxTracking from './WaxTracking.jsx';

const MainApp = () => {
  const { user, isAuthenticated, isLoading, loginWithRedirect, logout, error } = useAuth0();
  
  // Check if we have a valid session on mount
  React.useEffect(() => {
    console.log('Component mounted - checking session...');
    console.log('Current Auth0 state:', { user, isAuthenticated, isLoading });
  }, []);

  // Debug logging
  React.useEffect(() => {
    console.log('Auth0 State:', { user, isAuthenticated, isLoading, error });
    console.log('User object:', user);
    console.log('Is authenticated:', isAuthenticated);
    console.log('Is loading:', isLoading);
    if (error) {
      console.error('Auth0 Error:', error);
    }
  }, [user, isAuthenticated, isLoading, error]);

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Checking Authentication...</Typography>
        <Typography variant="body2" color="text.secondary">
          Verifying your session and loading Strava data
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 3,
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600, width: '100%' }}>
          <AlertTitle>Authentication Error</AlertTitle>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {error.message || 'An error occurred during authentication'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Error Details:</strong>
          </Typography>
          <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
            {JSON.stringify(error, null, 2)}
          </Typography>
        </Alert>
        
        <Box display="flex" gap={2}>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
            startIcon={<Error />}
          >
            Retry
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => loginWithRedirect()}
            startIcon={<Login />}
          >
            Try Login Again
          </Button>
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 3,
          p: 3,
        }}
      >
        {/* Whacks Off Branding */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <img 
            src="/icons/whacksoff.png" 
            alt="Whacks Off Logo" 
            style={{ 
              width: 80, 
              height: 80, 
              objectFit: 'contain',
              marginBottom: '16px'
            }} 
          />
          <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
            Whacks Off
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontStyle: 'italic', mb: 3 }}>
            Whacks On, Whacks Off
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 400, borderRadius: 2, background: 'linear-gradient(45deg, #fc4c02 30%, #ff6b35 90%)', color: 'white' }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Sign in to use your Strava activity data to track when your next chain wax is due
          </Typography>
          
          <Button
            variant="contained"
            size="large"
            onClick={() => loginWithRedirect()}
            startIcon={<Login />}
            sx={{
              backgroundColor: '#FC5200',
              '&:hover': {
                backgroundColor: '#E54700',
              },
              color: 'white',
              mb: 2,
              height: 48,
              px: 3,
              fontWeight: 'bold'
            }}
          >
            Connect with Strava
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Tooltip 
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Privacy & Data Usage
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • We only access your Strava activity data to track chain maintenance
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • No data is stored on external servers - everything stays on your device
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • We only read activity names and distances, never personal details
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • Compatible with Strava - you can revoke access anytime in your Strava settings
                  </Typography>
                </Box>
              }
              arrow
              placement="top"
              sx={{ 
                '& .MuiTooltip-tooltip': { 
                  maxWidth: 300,
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  fontSize: '0.875rem'
                }
              }}
            >
              <Link
                component="button"
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': {
                    color: 'white'
                  }
                }}
              >
                <PrivacyTip sx={{ fontSize: 16 }} />
                Privacy
              </Link>
            </Tooltip>
          </Box>
        </Paper>
        
        {/* Powered by Strava footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Powered by Strava
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Strava API integration for chain maintenance tracking
          </Typography>
        </Box>
      </Box>
    );
  }

  // User is authenticated - show layout with sidebar
  return (
    <Box sx={{ display: 'flex' }}>
      <Navigation />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - 240px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default'
        }}
      >
        <Routes>
          <Route path="/" element={
            <Box>
              <WaxTracking />
              
              {/* Powered by Strava footer */}
              <Box sx={{ mt: 4, textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Powered by Strava
                </Typography>
              </Box>
            </Box>
          } />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default MainApp;
